// Pages/almacenamiento/useAlmacenamientoLogic.ts
//
// Estado y datos de la página /almacenamiento: filtros (persistidos por
// usuario admin), resumen de salud del espacio, lista paginada en el servidor
// y selección entre páginas.
//
// Qué NO se persiste, a propósito: la selección de filas y la página actual.
// Restaurar una selección para borrar es peligroso (docs/filtros-persistentes.md §4).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  useFiltroPersistido,
  useRangoPersistido,
} from "../../hooks/useFiltroPersistido";
import { getMsg } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { propiedadesApi } from "../../services/propiedades.api";
import {
  almacenamientoApi,
  MAX_IDS_POR_OPERACION,
  type Archivo,
  type DireccionOrden,
  type EstadoFiltro,
  type OrdenArchivos,
  type ResumenAlmacenamiento,
  type TipoArchivo,
} from "../../services/almacenamiento.api";
import {
  ANTIGUEDADES_MESES,
  ESTADOS_VALIDOS,
  FILTROS_BASE,
  ORDENES_VALIDOS,
  PESOS_MIN_MB,
  TIPOS_VALIDOS,
  agregarASeleccion,
  construirParams,
  diaISO,
  filtrosDeAtajo,
  hayFiltrosActivos,
  huellaConjunto,
  huellaConOrden,
  parsearEnlaceDirecto,
  quitarDeSeleccion,
  totalSeleccion,
  type FiltrosArchivos,
  type IdAtajo,
} from "./almacenamientoUtils";

export type VistaAlmacenamiento = "archivos" | "registro";

export interface OpcionPropiedad {
  value: number;
  /** Texto visible (en MAYÚSCULAS). */
  label: string;
  /** Nombre crudo, para buscar. */
  nombre: string;
}

const TAMANO_PAGINA_INICIAL = 25;
const TAMANO_LOTE_SELECCION = 200;

const esTexto = (v: unknown) => v === undefined || typeof v === "string";
const esEnteroPositivo = (v: unknown) =>
  v === undefined || (typeof v === "number" && Number.isInteger(v) && v > 0);
const enLista = (lista: readonly unknown[]) => (v: unknown) =>
  v === undefined || lista.includes(v);

export function useAlmacenamientoLogic() {
  // ── Enlaces directos (?modulo=aseos&estado=huerfano…) ────────────────────
  // Mandan sobre lo guardado; se leen UNA vez al montar y se limpian de la URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const [enlace] = useState(() => parsearEnlaceDirecto(searchParams));
  const restaurar = !enlace.hayEnlace;
  useEffect(() => {
    if (enlace.hayEnlace) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filtros (se recuerdan por usuario admin) ─────────────────────────────
  const [vista, setVista] = useFiltroPersistido<VistaAlmacenamiento>(
    "almacenamiento.vista",
    "archivos",
    { validar: (v) => v === "archivos" || v === "registro", restaurar },
  );
  const [busqueda, setBusqueda] = useFiltroPersistido<string | undefined>(
    "almacenamiento.busqueda",
    enlace.valores.q,
    { validar: esTexto, restaurar },
  );
  const [modulo, setModulo] = useFiltroPersistido<string | undefined>(
    "almacenamiento.modulo",
    enlace.valores.modulo,
    { validar: esTexto, restaurar },
  );
  const [tipo, setTipo] = useFiltroPersistido<TipoArchivo | undefined>(
    "almacenamiento.tipo",
    enlace.valores.tipo,
    { validar: enLista(TIPOS_VALIDOS), restaurar },
  );
  const [propiedadId, setPropiedadId] = useFiltroPersistido<number | undefined>(
    "almacenamiento.propiedad",
    enlace.valores.propiedadId,
    { validar: esEnteroPositivo, restaurar },
  );
  const [rango, setRango] = useRangoPersistido("almacenamiento.rango", null, {
    restaurar,
  });
  const [antiguedadMeses, setAntiguedadMeses] = useFiltroPersistido<number | undefined>(
    "almacenamiento.antiguedad",
    undefined,
    { validar: enLista(ANTIGUEDADES_MESES), restaurar },
  );
  const [pesoMinMB, setPesoMinMB] = useFiltroPersistido<number | undefined>(
    "almacenamiento.pesoMin",
    undefined,
    { validar: enLista(PESOS_MIN_MB), restaurar },
  );
  const [estado, setEstado] = useFiltroPersistido<EstadoFiltro>(
    "almacenamiento.estado",
    enlace.valores.estado ?? FILTROS_BASE.estado,
    { validar: (v) => (ESTADOS_VALIDOS as unknown[]).includes(v), restaurar },
  );
  const [orden, setOrden] = useFiltroPersistido<OrdenArchivos>(
    "almacenamiento.orden",
    FILTROS_BASE.orden,
    { validar: (v) => (ORDENES_VALIDOS as unknown[]).includes(v), restaurar },
  );
  const [dir, setDir] = useFiltroPersistido<DireccionOrden>(
    "almacenamiento.dir",
    FILTROS_BASE.dir,
    { validar: (v) => v === "asc" || v === "desc", restaurar },
  );

  // Texto del buscador: espejo de `busqueda` (si divergieran se vería la caja
  // escrita con la tabla sin filtrar, o al revés).
  const [textoBusqueda, setTextoBusqueda] = useState<string>(() => busqueda ?? "");
  useEffect(() => {
    setTextoBusqueda(busqueda ?? "");
  }, [busqueda]);

  const desde = diaISO(rango?.[0]);
  const hasta = diaISO(rango?.[1]);

  const filtros: FiltrosArchivos = useMemo(
    () => ({
      q: busqueda,
      modulo,
      tipo,
      propiedadId,
      desde,
      hasta,
      antiguedadMeses,
      pesoMinMB,
      estado,
      orden,
      dir,
    }),
    [busqueda, modulo, tipo, propiedadId, desde, hasta, antiguedadMeses, pesoMinMB, estado, orden, dir],
  );
  const filtrosActivos = hayFiltrosActivos(filtros);

  /** Deja puestos EXACTAMENTE estos filtros (los atajos reemplazan todo). */
  const aplicarFiltros = useCallback(
    (f: FiltrosArchivos) => {
      setBusqueda(f.q || undefined);
      setModulo(f.modulo);
      setTipo(f.tipo);
      setPropiedadId(f.propiedadId);
      setRango(null);
      setAntiguedadMeses(f.antiguedadMeses);
      setPesoMinMB(f.pesoMinMB);
      setEstado(f.estado);
      setOrden(f.orden);
      setDir(f.dir);
    },
    [setBusqueda, setModulo, setTipo, setPropiedadId, setRango, setAntiguedadMeses, setPesoMinMB, setEstado, setOrden, setDir],
  );

  const aplicarAtajo = useCallback(
    (id: IdAtajo) => aplicarFiltros(filtrosDeAtajo(id)),
    [aplicarFiltros],
  );

  /** Quita los filtros pero conserva el orden elegido. */
  const limpiarFiltros = useCallback(
    () => aplicarFiltros({ ...FILTROS_BASE, orden, dir }),
    [aplicarFiltros, orden, dir],
  );

  // ── Propiedades para el filtro ───────────────────────────────────────────
  const [propiedades, setPropiedades] = useState<OpcionPropiedad[]>([]);
  useEffect(() => {
    let vivo = true;
    void propiedadesApi.getAll().then((res) => {
      if (!vivo || !res?.data) return;
      setPropiedades(
        res.data
          .map((p) => ({
            value: p.id,
            label: formatPropertyName(p.nombrePropiedad),
            nombre: String(p.nombrePropiedad ?? ""),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "es")),
      );
    });
    return () => {
      vivo = false;
    };
  }, []);

  // ── Resumen (salud del espacio) ──────────────────────────────────────────
  const [resumen, setResumen] = useState<ResumenAlmacenamiento | null>(null);
  const [cargandoResumen, setCargandoResumen] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [errorResumen, setErrorResumen] = useState(false);

  const cargarResumen = useCallback(async (rescanear: boolean) => {
    if (rescanear) setRefrescando(true);
    else setCargandoResumen(true);
    try {
      const res = rescanear
        ? await almacenamientoApi.refrescar()
        : await almacenamientoApi.resumen();
      if (res?.data) {
        setResumen(res.data);
        setErrorResumen(false);
      } else {
        // Un resumen que no se pudo leer NO se reemplaza por ceros: se avisa.
        setErrorResumen(true);
      }
    } finally {
      setCargandoResumen(false);
      setRefrescando(false);
    }
  }, []);

  useEffect(() => {
    void cargarResumen(false);
  }, [cargarResumen]);

  // ── Lista paginada en el servidor ────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(TAMANO_PAGINA_INICIAL);
  const [archivos, setArchivos] = useState<Archivo[]>([]);
  const [total, setTotal] = useState(0);
  const [bytesFiltrados, setBytesFiltrados] = useState(0);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [errorLista, setErrorLista] = useState(false);
  const peticion = useRef(0);

  const cargarLista = useCallback(
    async (p: number, s: number) => {
      const id = ++peticion.current;
      setCargandoLista(true);
      try {
        const res = await almacenamientoApi.archivos(construirParams(filtros, p, s));
        // Una respuesta vieja (otro filtro, otra página) no pisa a la nueva.
        if (id !== peticion.current) return;
        if (!res) {
          setErrorLista(true);
          return;
        }
        setErrorLista(false);
        setArchivos(res.data || []);
        setTotal(res.total || 0);
        setBytesFiltrados(res.totalBytesFiltrados || 0);
        setPage(res.page || p);
        setSize(res.size || s);
      } finally {
        if (id === peticion.current) setCargandoLista(false);
      }
    },
    [filtros],
  );

  useEffect(() => {
    if (vista !== "archivos") return;
    void cargarLista(1, size);
    // `size` se omite: cambiar el tamaño ya recarga desde onChange de la tabla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, vista]);

  const cambiarPagina = useCallback(
    (p: number, s: number) => {
      void cargarLista(s !== size ? 1 : p, s);
    },
    [cargarLista, size],
  );

  // ── Selección entre páginas ──────────────────────────────────────────────
  const [seleccion, setSeleccion] = useState<ReadonlyMap<string, Archivo>>(new Map());
  const [seleccionandoTodo, setSeleccionandoTodo] = useState(false);
  const totalSel = useMemo(() => totalSeleccion(seleccion), [seleccion]);

  // La selección vale solo para el filtro y la vista en que se hizo: lo marcado
  // y luego fuera de pantalla seguiría contando en «Eliminar» sin que nadie lo
  // vea. Al cambiar el conjunto (no el orden) o la vista se vacía, con aviso.
  const contexto = `${huellaConjunto(filtros)}|${vista}`;
  // Con orden incluido: paginar bajo otro orden salta o repite filas.
  const contextoConOrden = `${huellaConOrden(filtros)}|${vista}`;
  const contextoVigente = useRef(contextoConOrden);
  contextoVigente.current = contextoConOrden;
  const seleccionRef = useRef(seleccion);
  seleccionRef.current = seleccion;
  const contextoPrevio = useRef(contexto);
  useEffect(() => {
    if (contextoPrevio.current === contexto) return;
    contextoPrevio.current = contexto;
    if (seleccionRef.current.size > 0) {
      setSeleccion(new Map());
      getMsg()?.info("Se vació la selección porque cambiaron los filtros o la vista.");
    }
  }, [contexto]);

  const avisarTope = useCallback((omitidos: number) => {
    if (omitidos > 0) {
      getMsg()?.warning(
        `Solo se pueden eliminar ${MAX_IDS_POR_OPERACION} archivos por vez: ${omitidos} quedaron fuera de la selección.`,
      );
    }
  }, []);

  const alternarFilas = useCallback(
    (filas: Archivo[], marcar: boolean) => {
      setSeleccion((actual) => {
        if (!marcar) return quitarDeSeleccion(actual, filas.map((f) => f.id));
        const { seleccion: nueva, omitidos } = agregarASeleccion(actual, filas);
        if (omitidos > 0) avisarTope(omitidos);
        return nueva;
      });
    },
    [avisarTope],
  );

  const limpiarSeleccion = useCallback(() => setSeleccion(new Map()), []);

  /** Selecciona los archivos de TODO el filtro (hasta el tope por operación). */
  const seleccionarTodoElFiltro = useCallback(async () => {
    // Los filtros de ESTE momento: si cambian mientras se pagina, lo traído
    // pertenece a otra consulta y se descarta entero.
    const contextoInicial = contextoVigente.current;
    const filtrosInicial = filtros;
    setSeleccionandoTodo(true);
    try {
      let acumulado: ReadonlyMap<string, Archivo> = seleccionRef.current;
      const traidas: Archivo[] = [];
      for (let p = 1; p <= Math.ceil(MAX_IDS_POR_OPERACION / TAMANO_LOTE_SELECCION) + 1; p++) {
        const res = await almacenamientoApi.archivos(
          construirParams(filtrosInicial, p, TAMANO_LOTE_SELECCION),
        );
        if (!res) return; // apiFetch ya avisó
        if (contextoVigente.current !== contextoInicial) return;
        traidas.push(...(res.data || []));
        const { seleccion: nueva, omitidos } = agregarASeleccion(acumulado, res.data || []);
        acumulado = nueva;
        if (omitidos > 0 || p * TAMANO_LOTE_SELECCION >= res.total || !(res.data || []).length) break;
      }
      // Se funde con la selección de AHORA, no con la de hace unos segundos:
      // lo que el usuario marcó o quitó a mano mientras cargaba no se pisa.
      const { seleccion: final, omitidos } = agregarASeleccion(seleccionRef.current, traidas);
      setSeleccion(final);
      avisarTope(omitidos);
    } finally {
      setSeleccionandoTodo(false);
    }
  }, [filtros, avisarTope]);

  // ── Después de eliminar ──────────────────────────────────────────────────
  /** Reescanea (el espacio liberado tiene que verse de verdad) y recarga la lista. */
  const trasEliminar = useCallback(async () => {
    setSeleccion(new Map());
    await Promise.all([cargarResumen(true), cargarLista(1, size)]);
  }, [cargarResumen, cargarLista, size]);

  /**
   * Termina el relleno de versiones livianas: el peso de los videos cambió (suman su copia) y el
   * resumen trae el nuevo conteo de videos sin versión liviana. Recarga ambos SIN tocar la
   * selección ni la página (no se eliminó nada), y sin reescanear a la fuerza: el servidor ya
   * invalidó su índice al terminar.
   */
  const trasGenerarPreviews = useCallback(async () => {
    await Promise.all([cargarResumen(false), cargarLista(page, size)]);
  }, [cargarResumen, cargarLista, page, size]);

  return {
    vista,
    setVista,
    // filtros
    filtros,
    filtrosActivos,
    textoBusqueda,
    setTextoBusqueda,
    setBusqueda,
    setModulo,
    setTipo,
    setPropiedadId,
    rango: rango as [Dayjs | null, Dayjs | null] | null,
    setRango,
    setAntiguedadMeses,
    setPesoMinMB,
    setEstado,
    setOrden,
    setDir,
    aplicarAtajo,
    aplicarFiltros,
    limpiarFiltros,
    propiedades,
    // resumen
    resumen,
    cargandoResumen,
    refrescando,
    errorResumen,
    cargarResumen,
    // lista
    archivos,
    total,
    bytesFiltrados,
    page,
    size,
    cargandoLista,
    errorLista,
    cargarLista,
    cambiarPagina,
    // selección
    seleccion,
    totalSel,
    seleccionandoTodo,
    alternarFilas,
    limpiarSeleccion,
    seleccionarTodoElFiltro,
    trasEliminar: trasEliminar,
    trasGenerarPreviews,
  };
}
